import { createSlice, Draft, PayloadAction } from '@reduxjs/toolkit';

import { AnalysisHistoryRecord } from '@paragon/contract-library';
import { componentDesignsDeletedWithInsertions } from '@paragon/layout-models';

import { receiveComponentDesigns } from './componentDesignsSlice';

interface ComponentDesignHistory {
  readonly [analysisSetGuid: string]: AnalysisHistoryRecord;
}

export interface AnalysisHistoryState {
  readonly [componentDesignGuid: string]: ComponentDesignHistory | undefined;
}

const initialState: AnalysisHistoryState = {};

const analysisHistorySlice = createSlice({
  name: 'analysisHistory',
  initialState,
  reducers: {
    setAnalysisHistoryRecord: (state, action: PayloadAction<AnalysisHistoryRecord>) => {
      setAnalysisHistoryRecordInState(state, action.payload);
    },
    setAnalysisHistory: (state, action: PayloadAction<readonly AnalysisHistoryRecord[]>) => {
      for (const analysisHistoryRecord of action.payload) {
        setAnalysisHistoryRecordInState(state, analysisHistoryRecord);
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(componentDesignsDeletedWithInsertions, (state, action) => {
      for (const componentDesignGuid of action.payload.guids) {
        delete state[componentDesignGuid];
      }
    });

    builder.addCase(receiveComponentDesigns, (state, action) => {
      for (const analysisHistoryRecord of action.payload.history) {
        setAnalysisHistoryRecordInState(state, analysisHistoryRecord);
      }
    });
  },
});

function setAnalysisHistoryRecordInState(
  state: Draft<AnalysisHistoryState>,
  analysisHistoryRecord: AnalysisHistoryRecord,
) {
  const { analysisSetGuid, componentDesignGuid } = analysisHistoryRecord;

  const componentDesignHistory = state[componentDesignGuid];
  if (componentDesignHistory) {
    componentDesignHistory[analysisSetGuid] = analysisHistoryRecord;
  } else {
    state[componentDesignGuid] = { [analysisSetGuid]: analysisHistoryRecord };
  }
}

export const { setAnalysisHistoryRecord, setAnalysisHistory } = analysisHistorySlice.actions;
export const analysisHistory = analysisHistorySlice.reducer;
